import { map, filter, take, distinctUntilChanged } from 'rxjs';
import { store$, store } from '@redux/store';
import { userProfileSlice } from '../redux/slices';
import {
  selectFavorites,
  selectIsLoggedIn,
  selectIsProfileFavoritesLoaded,
  selectUserIdAndPendingSaves
} from '../redux/selectors';
import FavoriteType from '../enums/favoriteType';
import onOpenModal from './modal';
import getListingInformationFromDataset from '../services/getListingInformationForPendingSave';

const actions = userProfileSlice.actions;

// Get the favorite information from the card
function getCardInformation(card) {
    const { type } = card.dataset;
    const url = type === FavoriteType.Builder.toString()
                  ? card.dataset.url
                  : new URL(card.querySelector('a').href).pathname;
    const listingInformation = getListingInformationFromDataset(card.dataset)

    return {
      listingInformation,
      type: FavoriteType.fromKey(type).type,
      url,
    };
  }

// Check if a favorite is already saved
function isFavoriteSaved(id, favorites) {
  return favorites.some(favorite => favorite.listingId === Number(id));
}

// Dispatch the save/delete action
function dispatchSaveOrDeleteAction(payload, isSaved) {
  const action = isSaved ? actions.deleteFavorite(payload) : actions.saveFavorite(payload);
  store.dispatch(action);
}

/**
 * handles whether to save or delete a favorite from the user's profile on user's click.
 * @param {HTMLElement} icon the icon clicked by the user.
 */
function toggleFavorite(icon){
    const card = icon.closest('[data-card]') || icon.closest('[data-favorite-icon]');
    const { userId, favorites } = store.getState().userProfile;
    const cardInfo = getCardInformation(card);
    const isSaved = isFavoriteSaved(cardInfo.listingInformation.id, favorites);
    const payload = { ...cardInfo, userId };

    dispatchSaveOrDeleteAction(payload, isSaved);
}

/**
 * updates the favorites icon on each card to show if the listing is already saved as a favorite.
 * @param {Array[Object]} favorites list of favorites from the user's profile.
 */
function updateSavedFavorites(favorites) {
  const favoritesIds = new Set(favorites?.map(favorite => favorite.listingId) ?? []);

  let cards = document.querySelectorAll('[data-card]') ?? [];
  if (cards.length === 0) {
    cards = document.querySelectorAll('[data-favorite-icon]') ?? [];
  }

  cards.forEach(card => {
      const { id } = card.dataset;
      const isSaved = favoritesIds.has(Number(id));
      const favorite = card.querySelector('[data-favorite-trigger]');

      if(favorite){
        favorite.classList.toggle('nhs_Saved', isSaved);
        favorite.classList.toggle('nhs_Save', !isSaved);
        if (isSaved) {
              favorite.setAttribute('data-qa', 'favorite_saved');
          }
      }

      // Sync display state of the two server-rendered buttons to match the Redux state.
      // Server-rendered visibility can be stale after a favorite add/remove, so normalize it here
      // based on the latest store state.
      const saveButton = card.querySelector('[data-auto-open-modal-save-to-favorites]');
      const unsaveButton = card.querySelector('[data-favorite-unsave]');

      if (saveButton) {
        saveButton.style.display = isSaved ? 'none' : '';
      }

      if (unsaveButton) {
        unsaveButton.style.display = isSaved ? '' : 'none';
      }
  })
}

// Save pending favorites on initial load to handle logged-out users 
// that have favorites saved in the cookie from before they logged-in.
function savePendingFavorites(data) {
  const { userId, pendingSaves } = data;
  
  if(pendingSaves){
    pendingSaves.forEach(pendingSave => {

      // update the favorite with the new user id.
      store.dispatch(actions.saveFavorite({ ...pendingSave, userId }));
    });
  }
}


/*
Subscribe to the store$ observable to listen for changes. When the user is logged in and the favorites profile
is loaded, map the favorites and compare with the lasted version. If the favorites change, update the
favorite UI.
*/
store$.pipe(
  filter(selectIsLoggedIn),
  filter(selectIsProfileFavoritesLoaded),
  map(selectFavorites),
  distinctUntilChanged((previous, current) => JSON.stringify(previous) === JSON.stringify(current))
).subscribe(updateSavedFavorites);

/*
Subscribe to the store$ observable to listen for changes. When the user is logged in and the favorites profile
is loaded, map the pending favorites and the user id, because this scenarios handles and save the pending favcorites
when the user is not logged in. Take only the first value emmited in the observable.
*/
store$.pipe(
  filter(selectIsLoggedIn),
  filter(selectIsProfileFavoritesLoaded),
  map(selectUserIdAndPendingSaves),
  take(1)
).subscribe(savePendingFavorites);

/**
 * Using event delegation to handle clicks on the favorites icon.
 */
document.body.addEventListener('click', (event) => {
  const icon = event.target.closest('[data-favorite-trigger]');
  const state = store.getState();
  const isLogin = state?.userProfile?.isLogin;

  // if the click was inside the icon, save or delete the favorite.
  if (icon) {
    toggleFavorite(icon)

    if (!isLogin) {
      onOpenModal(event);
    }
  }
});

export default updateSavedFavorites;